import React from 'react';
import { X, AlertTriangle } from 'lucide-react';
import { designTokens } from '../styles/design-tokens';
import { useTheme } from '../context/ThemeContext';
import { Section, Field } from './education/Section';

export interface AmlSiteProperties {
  id: string | number;
  pad_name: string | null;
  pad_number: string | null;
  problem_code: string | null;
  severity: string | null;
  status: string | null;
  area_acres: number | null;
}

interface Props {
  site: AmlSiteProperties;
  onClose: () => void;
}

const PROBLEM_CODES: Record<string, { name: string; description: string }> = {
  S:   { name: 'Subsidence',            description: 'Ground surface collapse over abandoned underground workings. Active or latent.' },
  GO:  { name: 'Gob Pile / Refuse',     description: 'Coal refuse embankment. Combustion, leachate, and physical collapse risk.' },
  DS:  { name: 'Dangerous Slide',       description: 'Unstable slope with documented slide history. Active movement hazard.' },
  DPE: { name: 'Dangerous Pile/Embankment', description: 'Man-made fill that is structurally unsound or at risk of failure.' },
  SB:  { name: 'Surface Burning',       description: 'Exposed burning coal refuse. Fire, smoke, and surface collapse.' },
  UMF: { name: 'Underground Mine Fire', description: 'Smoldering coal in abandoned workings. Long-duration subsurface hazard.' },
  SA:  { name: 'Spoil Area',            description: 'Surface mine spoil pile. Slope stability and runoff concerns.' },
  IRW: { name: 'Industrial/Residential Waste', description: 'Legacy waste deposit associated with mining operation.' },
  CSL: { name: 'Clogged Stream Lands',  description: 'Stream blocked or degraded by mining sediment.' },
  SL:  { name: 'Slide',                 description: 'Historical slope failure attributable to mining.' },
  SP:  { name: 'Spoil Pile',            description: 'Overburden material from surface mining.' },
  HR:  { name: 'Highwall Remnant',      description: 'Exposed vertical rock face from surface mining.' },
  BE:  { name: 'Bench',                 description: 'Cut-and-fill bench from contour mining.' },
  PI:  { name: 'Pit Impoundment',       description: 'Open water-filled pit from surface mining.' },
  DP:  { name: 'Dangerous Pile',        description: 'Unstable waste pile.' },
};

const SEVERITY_COLORS: Record<string, string> = {
  emergency: '#dc2626',
  active: '#f97316',
  unfunded: '#eab308',
  completed: '#65a30d',
  unknown: '#a16207',
};

export const AmlSiteDetail: React.FC<Props> = ({ site, onClose }) => {
  const { theme } = useTheme();
  const sev = site.severity || 'unknown';
  const color = SEVERITY_COLORS[sev] || '#a16207';
  const info = site.problem_code ? PROBLEM_CODES[site.problem_code] : null;
  const sevLabel = sev.charAt(0).toUpperCase() + sev.slice(1);

  return (
    <div style={{
      width: designTokens.layout.detailPanelWidth, background: theme.background,
      borderLeft: `1px solid ${theme.border}`,
      display: 'flex', flexDirection: 'column', overflow: 'hidden', flexShrink: 0,
    }}>
      <div style={{
        padding: '16px', borderBottom: `1px solid ${theme.border}`,
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <AlertTriangle size={18} color={color} />
          <h2 style={{ fontSize: '16px', fontWeight: 700, margin: 0, color: theme.text }}>AML Problem Site</h2>
        </div>
        <button onClick={onClose} style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: '2px', color: theme.textSecondary,
        }}>
          <X size={18} />
        </button>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
        <div style={{
          padding: '10px 14px', marginBottom: '16px', borderRadius: '8px',
          background: color + '15', border: `1px solid ${color}30`,
        }}>
          <div style={{ fontSize: '14px', fontWeight: 700, color, marginBottom: '6px' }}>
            {info?.name || 'Abandoned Mine Land Feature'}
            <span style={{ fontSize: '11px', fontWeight: 600, marginLeft: '8px', opacity: 0.8 }}>· {sevLabel}</span>
          </div>
          <div style={{ fontSize: '12px', color: theme.text, lineHeight: 1.5 }}>
            {info?.description || 'Physical hazard from pre-SMCRA (1977) mining activity.'}
          </div>
        </div>

        <Section title="Site Details">
          <Field label="PAD Name" value={site.pad_name} />
          <Field label="PAD Number" value={site.pad_number} />
          <Field label="Problem Code" value={site.problem_code} />
          <Field label="Severity" value={sevLabel} />
          <Field label="Status" value={site.status} />
          {site.area_acres != null && (
            <Field label="Area" value={`${Number(site.area_acres).toFixed(1)} ac`} />
          )}
        </Section>

        <Section title="BTM Development Impact">
          <div style={{ fontSize: '12px', color: theme.text, lineHeight: 1.7 }}>
            <ImpactItem
              label="Physical Hazard"
              detail="Do not site pads, roads, or utilities over the polygon without reclamation. Subsidence, refuse combustion, and slope failures are active on unfunded sites."
              theme={theme}
            />
            <ImpactItem
              label="Reclamation Timing"
              detail={sev === 'emergency' ? 'Emergency-priority reclamation is funded and scheduled. Confirm schedule with WVDEP Office of Abandoned Mine Lands before LOI.'
                : sev === 'active' ? 'Active-priority site in the reclamation queue. Timing depends on annual AML grant cycle.'
                : sev === 'unfunded' ? 'Unfunded site — no near-term reclamation. Treat as a permanent exclusion unless the developer funds private remediation.'
                : sev === 'completed' ? 'Reclamation completed. Confirm post-reclamation certification and residual engineering controls.'
                : 'Severity not categorized. Request WVDEP file for current status.'}
              theme={theme}
            />
            <ImpactItem
              label="CERCLA Liability"
              detail="Waste-bearing features (IRW, GO piles) may trigger CERCLA investigation. Pre-acquisition Phase I ESA is mandatory; Phase II likely if the parcel intersects waste polygons."
              theme={theme}
            />
            <ImpactItem
              label="Site Assembly"
              detail="Exclude polygon acreage from developable area. A 2,500-ac assembly with 10% AML effectively offers ~2,250 ac of buildable land."
              theme={theme}
            />
          </div>
        </Section>

        <Section title="Data Source">
          <div style={{ fontSize: '11px', color: theme.textTertiary, lineHeight: 1.6 }}>
            WVDEP Office of Abandoned Mine Lands and Reclamation (AML Inventory).
          </div>
        </Section>
      </div>
    </div>
  );
};

const ImpactItem: React.FC<{ label: string; detail: string; theme: ReturnType<typeof useTheme>['theme'] }> = ({ label, detail, theme }) => (
  <div style={{ marginBottom: '10px', paddingLeft: '10px', borderLeft: `2px solid ${theme.borderLight}` }}>
    <div style={{ fontSize: '12px', fontWeight: 600, color: theme.text, marginBottom: '2px' }}>{label}</div>
    <div style={{ fontSize: '11px', color: theme.textSecondary, lineHeight: 1.5 }}>{detail}</div>
  </div>
);
